import api from "../pages/commission/api";
import React, { createContext, useMemo } from "react";
import { useQuery } from "react-query";
import camelCase from "lodash/camelCase";

interface Setting {
  id: number;
  name: string;
  value: string;
}

type SettingInContext = Record<string, string | number | boolean>;

interface SettingsContextValue {
  settings: SettingInContext;
  refreshSettings: () => void;
  isLoading: boolean;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: {},
  refreshSettings: () => {},
  isLoading: false,
});

interface SettingsContextProps {
  settings: string[];
  initialSettings?: SettingInContext;
  providedSettings?: SettingInContext;
}

const SettingsContextProvider: React.FC<SettingsContextProps> = ({
  settings,
  initialSettings,
  providedSettings,
  children,
  ...props
}) => {
  const { data, refetch, isLoading } = useQuery<any>(
    ["settings", { settings }],
    () =>
      api.getSettings({
        fields: "name,value,type",
        "filter[name][in]": settings.join(","),
        "page[limit]": -1,
      }),
    {
      initialData: providedSettings || initialSettings,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: settings?.length > 0 && !providedSettings,
      select: (data: { data: { data: Setting[] } }) => {
        const parsedSettings: Record<string, any> = {};
        data?.data?.data?.forEach((setting: any) => {
          // convert snake_case to camelCase in setting name
          const name = camelCase(setting.name);
          parsedSettings[name] = parseSettings(setting);
        });
        return parsedSettings;
      },
    }
  );

  const value = useMemo(
    () => ({
      settings: providedSettings
        ? (providedSettings as SettingInContext)
        : (data as SettingInContext),
      refreshSettings: refetch,
      isLoading,
    }),
    [data, refetch, isLoading, providedSettings]
  );
  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

const useSettings = () => {
  const context = React.useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsContext");
  }
  return context;
};

const parseSettings = (setting: any) => {
  let parsedSetting: number | boolean | string;

  switch (setting.type) {
    case "bol":
      parsedSetting = setting.value === "true" ? true : false;
      break;
    case "int":
      parsedSetting = parseInt(setting.value);
      break;
    case "str":
      parsedSetting = setting.value;
      break;
    default:
      throw new Error(
        `Unsupported setting type ${setting.type} for setting ${setting.name}`
      );
  }
  return parsedSetting;
};

export { SettingsContext, useSettings, parseSettings };
export default SettingsContextProvider;
